import { Clock, FolderOpen, Heart, Lock, Play, Users } from 'lucide-react';
import { Link } from 'react-router';
import { EmptyState, SectionHeader, Surface } from '@/design/primitives';
import { greetingForHour } from './greeting';

/**
 * The landing screen.
 *
 * Two decisions carry it. First, generous space: this is the room you stand in
 * before choosing a film, and crowding it makes the whole application feel
 * cheaper than it is. Second, the two modes are given equal weight at the top,
 * because choosing between watching together and watching alone is genuinely
 * the first decision, not a setting buried elsewhere.
 */
export function HomeScreen() {
  const greeting = greetingForHour(new Date().getHours());

  return (
    <div className="mx-auto w-full max-w-6xl px-5 pb-16 pt-6 sm:px-8 sm:pb-20 sm:pt-12">
      <header className="mb-9">
        <h1 className="text-[1.75rem] font-semibold tracking-[-0.02em] text-text-1">{greeting}</h1>
        <p className="mt-1.5 text-sm text-text-3">Pick up where you left off, or start something new.</p>
      </header>

      <div className="mb-12 grid gap-4 sm:grid-cols-2">
        <ModeCard
          to="/together"
          icon={Users}
          title="Watch Together"
          description="Create a room and stay in step with your brother, frame for frame."
          available
        />
        <ModeCard
          to="/private"
          icon={Lock}
          title="Private Viewing"
          description="A separate space. Nothing watched here reaches the shared side."
          available
        />
      </div>

      <section className="mb-12" aria-labelledby="continue-heading">
        <SectionHeader
          title="Continue Watching"
          description="Films you have started but not finished."
        />
        <div id="continue-heading" className="sr-only">
          Continue Watching
        </div>
        <EmptyState
          icon={Play}
          title="Nothing in progress"
          description="Open a film and it will appear here, holding your place to the second."
        />
      </section>

      <section className="mb-12" aria-label="Recently Opened">
        <SectionHeader title="Recently Opened" description="The last things you played." />
        <EmptyState
          icon={FolderOpen}
          title="No history yet"
          description="Files you open are remembered here, so getting back to one takes a single click."
          compact
        />
      </section>

      <section aria-label="Favorites">
        <SectionHeader title="Favorites" description="Films you have marked to keep close." />
        <EmptyState
          icon={Heart}
          title="No favorites yet"
          description="Mark a film as a favorite and it will sit here, whichever mode you are in."
          compact
        />
      </section>
    </div>
  );
}

/**
 * One of the two large choices at the top of the screen.
 *
 * The gradient wash is a single low-opacity accent tint rather than a
 * multi-stop gradient. It gives the card presence without becoming the loud,
 * saturated slab that gradients usually turn into.
 */
function ModeCard({
  to,
  icon: Icon,
  title,
  description,
  available,
}: {
  readonly to: string;
  readonly icon: typeof Users;
  readonly title: string;
  readonly description: string;
  readonly available: boolean;
}) {
  const body = (
    <>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-accent-soft to-transparent opacity-70"
      />
      <span className="relative flex flex-col gap-3">
        <span className="flex size-10 items-center justify-center rounded-[var(--radius-md)] bg-accent text-text-on-accent shadow-2">
          <Icon size={18} strokeWidth={2.1} aria-hidden="true" />
        </span>
        <span className="block">
          <span className="block text-[1.0625rem] font-semibold tracking-tight text-text-1">
            {title}
          </span>
          <span className="mt-1 block text-[0.8125rem] leading-relaxed text-text-2">
            {description}
          </span>
        </span>
        {!available && (
          <span className="mt-1 inline-flex w-fit items-center gap-1.5 rounded-full bg-surface-3 px-2.5 py-1 text-[0.6875rem] font-medium text-text-3">
            <Clock size={11} strokeWidth={2.2} aria-hidden="true" />
            Not built yet
          </span>
        )}
      </span>
    </>
  );

  const surfaceClasses =
    'relative overflow-hidden p-6 min-h-[11rem] flex items-start ' +
    'transition-[transform,box-shadow] duration-[calc(var(--duration-base)*var(--motion-scale))]';

  // An unavailable destination is not a link. Rendering one that goes nowhere
  // would be a lie to both the pointer and the screen reader.
  if (!available) {
    return (
      <Surface material="glass" radius="xl" className={surfaceClasses} aria-disabled="true">
        {body}
      </Surface>
    );
  }

  return (
    <Link to={to} className="group block rounded-[var(--radius-xl)]">
      <Surface
        material="glass"
        radius="xl"
        className={`${surfaceClasses} group-hover:-translate-y-0.5 group-hover:shadow-4`}
      >
        {body}
      </Surface>
    </Link>
  );
}
